import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedTenant = localStorage.getItem('tenant');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      if (storedTenant) {
        setTenant(JSON.parse(storedTenant));
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password, tenantId, isPlatformAdmin = false) => {
    try {
      const endpoint = isPlatformAdmin 
        ? '/platform/admin/login' 
        : `/auth/login?tenant_id=${tenantId}`;
      
      const response = await axios.post(`${API}${endpoint}`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('userType', isPlatformAdmin ? 'platform_admin' : 'client');
      
      setToken(access_token);
      setUser(userData);
      
      return { success: true, userType: isPlatformAdmin ? 'platform_admin' : 'client' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const signup = async (companyName, email, password, adminName, phone) => {
    try {
      const response = await axios.post(`${API}/signup`, null, {
        params: { company_name: companyName, email, password, admin_name: adminName, phone }
      });
      
      const { access_token, user: userData, tenant: tenantData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('tenant', JSON.stringify(tenantData));
      localStorage.setItem('userType', 'client');
      
      setToken(access_token);
      setUser(userData);
      setTenant(tenantData);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Signup failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    localStorage.removeItem('userType');
    setToken(null);
    setUser(null);
    setTenant(null);
  };

  const isPlatformAdmin = () => {
    return localStorage.getItem('userType') === 'platform_admin';
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      tenant,
      token,
      loading,
      login,
      signup,
      logout,
      isPlatformAdmin,
      getAuthHeaders,
      api,
      API,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
